import { useMemo, useState } from 'react';
import type { Board } from '../lib/types';
import {
  addressLine,
  delta as formatDelta,
  driftTone,
  hhmm,
  secondsIn,
  statusLabel,
} from '../lib/format';
import { RouteMap } from './RouteMap';
import { StatusDot } from './Delta';
import { ListControls, ListGroup, type ListOption } from '@allrouted/ui/ListControls';
import { EyeIcon, IconButton } from '@allrouted/ui/Icons';
import { Resizer, usePaneSize } from '@allrouted/ui/Resizer';

/**
 * Tracking: where the fleet is, right now.
 *
 * The board answers "how is the day going" across every route at once. This
 * answers "where is this van", which is the question a phone call asks, so it
 * is a list of vehicles first and a map second. Selecting one hides the rest of
 * the fleet rather than highlighting it -- following a single technician across
 * a metro is hard enough without eleven other routes drawn over the top.
 *
 * Everything here comes from the same live board the dispatch screen patches
 * from the SSE stream, so the two can never disagree about who is late.
 *
 * The list can be searched, ordered and grouped. The grouping dimensions are
 * the ones our data actually has -- what a technician is doing, where they
 * start, whether they are behind. The incumbent offers a "type" we have never
 * seen the definition of, so it is not invented here.
 */

type SortKey = 'name' | 'activity' | 'progress' | 'drift';
type GroupKey = 'none' | 'status' | 'depot' | 'drift';

const SORTS: ListOption<SortKey>[] = [
  { value: 'name', label: 'Name' },
  { value: 'activity', label: 'Last activity' },
  { value: 'progress', label: 'Progress' },
  { value: 'drift', label: 'Drift' },
];

const GROUPS: ListOption<GroupKey>[] = [
  { value: 'none', label: 'No grouping' },
  { value: 'status', label: 'Status' },
  { value: 'depot', label: 'Depot' },
  { value: 'drift', label: 'Running late' },
];

/** Where a technician is up to, as a heading someone would say out loud. */
function statusGroup(done: number, total: number, started: number | null): string {
  if (total > 0 && done === total) return 'Finished';
  if (started !== null || done > 0) return 'Working';
  return 'Not started';
}

export function TrackingView({ board }: { board: Board | null }) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<SortKey>('name');
  const [ascending, setAscending] = useState(true);
  const [group, setGroup] = useState<GroupKey>('none');

  const list = usePaneSize('allrouted.tracking.list', 264, { min: 200, max: 520 });
  const stops = usePaneSize('allrouted.tracking.stops', 260, { min: 120, max: 600 });

  const zone = board?.territory.timezone ?? 'UTC';

  const rows = useMemo(() => {
    if (!board) return [];
    const positionByVehicle = new Map(
      board.positions.filter((p) => p.vehicleId).map((p) => [p.vehicleId as string, p]),
    );

    return board.routes.map((route) => {
      const done = route.stops.filter(
        (s) => s.status === 'completed' || s.status === 'failed',
      ).length;
      const next = route.stops.find(
        (s) => s.status !== 'completed' && s.status !== 'failed',
      );
      const fix = positionByVehicle.get(route.vehicleId) ?? null;

      /*
       * The last sign of life, as seconds since midnight so it sorts against
       * the plan's own clock. A GPS fix and a reported arrival are both
       * activity; take whichever came later.
       */
      const reported = route.stops.reduce<number | null>((latest, stop) => {
        const at = stop.actualDeparture ?? stop.actualArrival;
        return at !== null && (latest === null || at > latest) ? at : latest;
      }, null);
      const fixSeconds = fix ? secondsIn(zone, new Date(fix.at)) : null;
      const activity =
        reported === null
          ? fixSeconds
          : fixSeconds === null
            ? reported
            : Math.max(reported, fixSeconds);

      return {
        id: route.id,
        name: route.technicianName ?? route.vehicleName,
        vehicle: route.vehicleName,
        depot: route.startDepot?.name ?? 'No depot',
        done,
        total: route.stops.length,
        next,
        delta: route.deltaSeconds,
        started: route.actualStart,
        activity,
        fix,
        stops: route.stops,
      };
    });
  }, [board, zone]);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const matched = needle
      ? rows.filter((r) =>
          [r.name, r.vehicle, r.depot, r.next?.orderName]
            .filter(Boolean)
            .some((field) => String(field).toLowerCase().includes(needle)),
        )
      : rows;

    const direction = ascending ? 1 : -1;
    const sorted = [...matched].sort((a, b) => {
      switch (sort) {
        case 'activity':
          /*
           * A vehicle that has reported nothing sorts last either way. It is
           * not "earliest activity"; it is no activity, and burying it under
           * the ascending list would hide exactly the van worth chasing.
           */
          if (a.activity === null || b.activity === null) {
            return a.activity === b.activity ? 0 : a.activity === null ? 1 : -1;
          }
          return (a.activity - b.activity) * direction;
        case 'progress':
          return (a.done / (a.total || 1) - b.done / (b.total || 1)) * direction;
        case 'drift':
          return (a.delta - b.delta) * direction;
        default:
          return a.name.localeCompare(b.name) * direction;
      }
    });
    return sorted;
  }, [rows, query, sort, ascending]);

  /** The list as sections. One unnamed section when nothing is grouped. */
  const sections = useMemo(() => {
    if (group === 'none') return [{ label: '', rows: visible }];

    const buckets = new Map<string, typeof visible>();
    for (const row of visible) {
      const key =
        group === 'status'
          ? statusGroup(row.done, row.total, row.started)
          : group === 'depot'
            ? row.depot
            : row.delta > 300
              ? 'Running late'
              : row.delta > 0
                ? 'Slipping'
                : 'On time';
      const bucket = buckets.get(key);
      if (bucket) bucket.push(row);
      else buckets.set(key, [row]);
    }
    return [...buckets.entries()].map(([label, rows]) => ({ label, rows }));
  }, [visible, group]);

  const selected = useMemo(
    () => rows.find((r) => r.id === selectedId) ?? null,
    [rows, selectedId],
  );

  /* Selecting one technician means seeing one technician. */
  const hidden = useMemo(() => {
    if (!selectedId || !board) return undefined;
    /* Only real routes are on the map, so only they can be hidden from it. */
    return new Set(
      board.routes
        .map((r) => r.id)
        .filter((id): id is string => Boolean(id) && id !== selectedId),
    );
  }, [selectedId, board]);

  if (!board) return <div className="empty">No plan for this day yet.</div>;

  return (
    <div
      className="tracking-layout"
      style={{ gridTemplateColumns: `${list.size}px auto 1fr` }}
    >
      <div className="master">
        <div className="side-head">
          <h2>Vehicles</h2>
          <span className="count-pill">
            {visible.length === rows.length ? rows.length : `${visible.length}/${rows.length}`}
          </span>
          {selectedId ? (
            <IconButton small label="Show the whole fleet" onClick={() => setSelectedId(null)}>
              <EyeIcon />
            </IconButton>
          ) : null}
        </div>

        <div className="search" style={{ margin: '0 10px 8px' }}>
          <input
            type="text"
            value={query}
            placeholder="Search vehicle, driver, depot"
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search vehicles"
          />
        </div>

        <ListControls
          sorts={SORTS}
          sort={sort}
          onSort={setSort}
          ascending={ascending}
          onDirection={setAscending}
          groups={GROUPS}
          group={group}
          onGroup={setGroup}
        />
        <div className="side-list">
          {rows.length === 0 ? (
            <div className="empty">No routes on this day.</div>
          ) : visible.length === 0 ? (
            <div className="empty">Nothing matches that search.</div>
          ) : (
            sections.map((section) => (
              <div key={section.label || 'all'}>
                {section.label ? (
                  <ListGroup label={section.label} count={section.rows.length} />
                ) : null}
                {section.rows.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    className="order-row"
                    data-selected={r.id === selectedId ? 'true' : undefined}
                    onClick={() => setSelectedId(r.id === selectedId ? null : r.id)}
                  >
                    <b>{r.name}</b>
                    <span className="delta" data-tone={driftTone(r.delta)}>
                      {r.delta === 0 ? 'on time' : formatDelta(r.delta)}
                    </span>
                    <small>
                      {r.done} of {r.total} done
                      {r.next ? ` · next ${r.next.orderName}` : ' · finished'}
                      {/* The list carries the fleet facts; the panel below shows stops. */}
                      {r.activity === null ? ' · no activity' : ` · last ${hhmm(r.activity)}`}
                    </small>
                  </button>
                ))}
              </div>
            ))
          )}
        </div>
      </div>

      <Resizer orientation="vertical" onResize={list.onResize} label="Resize the vehicle list" />

      <div
        className="tracking-main"
        style={{ gridTemplateRows: `1fr auto ${stops.size}px` }}
      >
        <RouteMap
          board={board}
          selectedRouteId={selectedId}
          hiddenRouteIds={hidden}
          onSelectRoute={setSelectedId}
        />

        {/* Dragging down shrinks the stops below it. */}
        <Resizer
          orientation="horizontal"
          direction={-1}
          onResize={stops.onResize}
          label="Resize the stop list"
        />

        <div className="tracking-detail">
          {selected ? (
            <div className="table-scroll">
              <table className="grid">
                <thead>
                  <tr>
                    <th className="num">#</th>
                    <th>Order</th>
                    <th>Address</th>
                    <th className="num">Window</th>
                    <th className="num">Planned</th>
                    <th className="num">{selected.done === selected.total ? 'Arrived' : 'ETA'}</th>
                    <th className="num">Drift</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {selected.stops.map((stop) => (
                    <tr key={stop.id}>
                      <td className="num">{stop.sequence}</td>
                      <td>
                        <StatusDot status={stop.status} />
                        <b style={{ marginLeft: 6 }}>{stop.orderName}</b>
                      </td>
                      <td>{addressLine(stop.address)}</td>
                      <td className="num">
                        {stop.timeWindows.length > 0
                          ? stop.timeWindows
                              .map((w) => `${hhmm(w.start)}-${hhmm(w.end)}`)
                              .join(', ')
                          : '--'}
                      </td>
                      <td className="num">{hhmm(stop.plannedArrival)}</td>
                      <td className="num">
                        {hhmm(stop.actualArrival ?? stop.projectedArrival)}
                      </td>
                      <td className="num">
                        <span className="delta" data-tone={driftTone(stop.deltaSeconds)}>
                          {stop.deltaSeconds ? formatDelta(stop.deltaSeconds) : '--'}
                        </span>
                      </td>
                      <td>{statusLabel(stop.status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            /*
             * Deliberately not a second copy of the fleet. The list on the left
             * already answers "who is where"; this space answers "what is that
             * technician doing", which needs one of them picked first.
             */
            <div className="empty">Select a technician to see their stops.</div>
          )}
        </div>
      </div>
    </div>
  );
}
